import { cn } from '@simplywallst/ui-core/next';
import { useImageLoading } from './hooks/useImageLoading';
import { hasValidImage } from './constants';
import {
  useCloudflareImage,
  useResponsiveCloudflareImage,
} from '@/hooks/useCloudflareImage';

// Avatar-specific image configuration
const AVATAR_IMAGE_CONFIG = {
  quality: {
    placeholder: 10,
    mobile: 70,
    tablet: 75,
    desktop: 80,
  },
  placeholderBlur: 20,
} as const;

// Create avatar-specific responsive breakpoints
const createAvatarBreakpoints = (
  mobileSize: number,
  tabletSize: number,
  desktopSize: number
) => [
  // Mobile (1x)
  {
    width: mobileSize,
    height: mobileSize,
    quality: AVATAR_IMAGE_CONFIG.quality.mobile,
  },
  // Tablet (1x)
  {
    width: tabletSize,
    height: tabletSize,
    quality: AVATAR_IMAGE_CONFIG.quality.tablet,
  },
  // Desktop (1x)
  {
    width: desktopSize,
    height: desktopSize,
    quality: AVATAR_IMAGE_CONFIG.quality.desktop,
  },
  // Mobile (2x for high-DPI)
  {
    width: mobileSize * 2,
    height: mobileSize * 2,
    quality: AVATAR_IMAGE_CONFIG.quality.mobile,
  },
  // Tablet (2x for high-DPI)
  {
    width: tabletSize * 2,
    height: tabletSize * 2,
    quality: AVATAR_IMAGE_CONFIG.quality.tablet,
  },
  // Desktop (2x for high-DPI)
  {
    width: desktopSize * 2,
    height: desktopSize * 2,
    quality: AVATAR_IMAGE_CONFIG.quality.desktop,
  },
];

interface AvatarImageProps {
  src: string;
  alt: string;
  mobileSize: number;
  tabletSize: number;
  desktopSize: number;
  priority?: boolean;
  className?: string;
}

export const AvatarImage = ({
  src,
  alt,
  mobileSize,
  tabletSize,
  desktopSize,
  priority = false,
  className,
}: AvatarImageProps) => {
  const {
    isLoaded,
    showPlaceholder,
    hasError,
    imgRef,
    handleImageLoad,
    handleImageError,
  } = useImageLoading(priority);

  const responsiveBreakpoints = createAvatarBreakpoints(
    mobileSize,
    tabletSize,
    desktopSize
  );

  const { srcSet } = useResponsiveCloudflareImage({
    src,
    breakpoints: responsiveBreakpoints,
  });

  const placeholderUrl = useCloudflareImage({
    src,
    width: mobileSize,
    height: mobileSize,
    quality: AVATAR_IMAGE_CONFIG.quality.placeholder,
    blur: AVATAR_IMAGE_CONFIG.placeholderBlur,
  });

  const mobileImageUrl = useCloudflareImage({
    src,
    width: mobileSize,
    height: mobileSize,
    quality: AVATAR_IMAGE_CONFIG.quality.mobile,
  });

  const isValidImage = hasValidImage(src);

  if (!isValidImage || hasError) {
    return null;
  }

  const sizes = `${mobileSize}px`;

  return (
    <div className={cn('absolute inset-0', className)}>
      {showPlaceholder && placeholderUrl && (
        <img
          src={placeholderUrl}
          alt=""
          className="absolute inset-0 rounded-full blur-sm"
          aria-hidden="true"
        />
      )}
      <img
        ref={imgRef}
        src={mobileImageUrl}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        className={cn(
          'absolute inset-0 rounded-full transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
        loading={priority ? 'eager' : 'lazy'}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};
